import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useMarketing } from 'src/public/components/online_ordering/MarketingContext'
import { dummyAddressValue } from 'src/public/components/online_ordering/addressUtils'
import InputField from 'src/shared/components/common/form_input/InputField'
import { t } from 'src/translations'
import { isValidCustomerEmail } from 'shared/js/emailUtils'

import AnimatedSection from 'public/components/default_template/online_ordering/checkout/AnimatedSection'
import CheckoutSection from 'public/components/default_template/online_ordering/checkout/CheckoutSection'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import {
  PaymentOption,
  usePayment
} from 'public/components/online_ordering/PaymentContext'

import { getCustomerInfo } from './checkoutUtils'
import {
  MarketingSubscriptionCheckbox,
  MarketingSubscriptionCheckboxLegalText
} from './marketing/MarketingSubscriptionCheck'
import CreateAccountCheckbox from 'public/components/default_template/online_ordering/checkout/CreateAccountCheckbox'
import { ToastPaymentType } from 'src/public/components/online_ordering/types'
import { InternationalPhoneInput } from 'shared/components/common/form_input/InternationalPhoneInput'
import { useResetDisableAccountCreation } from './useResetDisableAccountCreation'

export const CustomerInfoSection = () => {
  const { customer } = useCustomer()
  const [editing, setEditing] = useState(false)
  const { paymentOption, userInfoRequired, paymentType } = usePayment()
  const { trigger } = useFormContext()

  const required = useMemo(
    () =>
      paymentType !== ToastPaymentType.APPLE_PAY &&
      (paymentOption === PaymentOption.UponReceipt || userInfoRequired),
    [paymentOption, userInfoRequired, paymentType]
  )

  const isCustomerPhoneValid = useMemo(
    () => isValidPhoneNumber(`+${customer?.phone}`),
    [customer]
  )

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case ' ':
        case 'Enter':
          setEditing(!editing)
          break
      }
    },
    [editing, setEditing]
  )

  return (
    <CheckoutSection
      title={t('customer-info-section.your-info')}
      secondaryHeaderContent={
        customer && editing ? (
          <button
            className='editLink'
            aria-label={t('customer-info-section.update-your-user-info')}
            onKeyDown={onKeyDown}
            onClick={async () => {
              const valid = await trigger(['yourInfoEmail'])
              if (valid) {
                setEditing(false)
              }
            }}
          >
            {t('customer-info-section.update')}
          </button>
        ) : undefined
      }
    >
      <AnimatedSection expanded={Boolean(customer) || required} slowTransition>
        <div
          data-testid={
            customer ? 'toast-checkout-inputs' : 'guest-checkout-inputs'
          }
          role='form'
        >
          <AnimatedSection
            expanded={isCustomerPhoneValid && !editing}
            slowTransition
            testid='completed-info-animated-section'
          >
            {isCustomerPhoneValid && !editing && (
              <CompletedInfo
                customer={customer}
                onClickEdit={() => setEditing(true)}
              />
            )}
          </AnimatedSection>
          <AnimatedSection
            expanded={!isCustomerPhoneValid || editing}
            slowTransition
            testid='customer-info-inputs-animated-section'
          >
            {(!isCustomerPhoneValid || editing) && (
              <CustomerInfoInputs required={required} />
            )}
          </AnimatedSection>
        </div>
      </AnimatedSection>
    </CheckoutSection>
  )
}

interface CustomerInfoInputsProps {
  required: boolean
}

const CustomerInfoInputs = ({ required }: CustomerInfoInputsProps) => {
  const {
    smsAccountEnabled,
    subscribeToSmsMarketing,
    setSubscribeToSmsMarketing,
    subscribeToEmailMarketing,
    setSubscribeToEmailMarketing
  } = useMarketing()
  const { customer } = useCustomer()
  const {
    formState: { errors, touchedFields },
    trigger,
    clearErrors
  } = useFormContext()
  const { cart } = useCart()
  const [zipcode, setZipCode] = useState(
    cart?.order?.deliveryInfo?.zipCode === dummyAddressValue
      ? ''
      : cart?.order?.deliveryInfo?.zipCode
  )

  // Show the "Create a Toast account" checkbox if the user is not logged in
  // and account creation hasn't been disabled due to passwordless login errors.
  const { disableAccountCreation } = useCheckout()
  const shouldShowCreateAccountCheckbox = !customer && !disableAccountCreation
  const phoneNumber = useWatch({ name: 'yourInfoPhone' })

  const validateEmail = useCallback(
    (value: string) =>
      required && !isValidCustomerEmail(value) && touchedFields?.yourInfoEmail
        ? t('customer-info-section.enter-valid-email-address')
        : true,
    [required, touchedFields]
  )

  // only display zipcode input for certain countries when no valid zipcode found in deliveryInfo
  const [includeZipCodeCollection, zipLabel] = useMemo(() => {
    const deliveryInfo = cart?.order?.deliveryInfo
    const collect =
      (deliveryInfo?.country === 'IE' || deliveryInfo?.country === 'GB') &&
      cart?.diningOptionBehavior === DiningOptionBehavior.Delivery &&
      (!deliveryInfo.zipCode || deliveryInfo.zipCode === dummyAddressValue)
    const label =
      deliveryInfo?.country === 'IE'
        ? t('customer-info-section.eircode')
        : t('customer-info-section.postcode')
    return [collect, label]
  }, [cart?.order?.deliveryInfo, cart?.diningOptionBehavior])

  useEffect(() => {
    if (!required) {
      // clear errors and trigger validation so that the form can be submitted when these fields are no longer required
      clearErrors([
        'yourInfoPhone',
        'yourInfoEmail',
        'yourInfoFirstName',
        'yourInfoLastName'
      ])
      trigger([
        'yourInfoPhone',
        'yourInfoEmail',
        'yourInfoFirstName',
        'yourInfoLastName'
      ])
    }
  }, [required, clearErrors, trigger])

  // Reset account creation when phone number changes
  useResetDisableAccountCreation()

  const defaultCustomerValues = getCustomerInfo(customer)

  return (
    <>
      <InternationalPhoneInput
        value={phoneNumber || ''}
        id='yourInfoPhone'
        required={required}
        label={t('pwless-auth.phone-number')}
        unregisterOnUnmount={false}
      />
      <InputField
        id='yourInfoEmail'
        type='email'
        autoComplete='email'
        label={t('customer-info-section.email')}
        defaultValue={defaultCustomerValues.yourInfoEmail}
        required={required}
        validate={validateEmail}
        preventFormSubmit
      />
      <InputField
        id='yourInfoFirstName'
        type='text'
        autoComplete='given-name'
        label={t('customer-info-section.first-name')}
        defaultValue={defaultCustomerValues.yourInfoFirstName}
        required={required}
        preventFormSubmit
      />
      <InputField
        id='yourInfoLastName'
        type='text'
        autoComplete='family-name'
        label={t('customer-info-section.last-name')}
        defaultValue={defaultCustomerValues.yourInfoLastName}
        required={required}
      />
      {includeZipCodeCollection && (
        <InputField
          id='deliveryZipCode'
          type='text'
          label={zipLabel}
          autoComplete='zipcode'
          preventFormSubmit
          value={zipcode || ''}
          required={required}
          onChange={(e) => {
            setZipCode(e.target.value)
          }}
        />
      )}
      {shouldShowCreateAccountCheckbox && <CreateAccountCheckbox />}
      {!customer && smsAccountEnabled && (
        <MarketingSubscriptionCheckbox
          name='subscribeToSmsMarketing'
          legalText={MarketingSubscriptionCheckboxLegalText.SMS}
          isActive={subscribeToSmsMarketing}
          setActive={setSubscribeToSmsMarketing}
          disabled={
            !phoneNumber || Object.keys(errors).includes('yourInfoPhone')
          }
        />
      )}
      {!customer && (
        <MarketingSubscriptionCheckbox
          name='subscribeToEmailMarketing'
          legalText={MarketingSubscriptionCheckboxLegalText.EMAIL}
          isActive={subscribeToEmailMarketing}
          setActive={setSubscribeToEmailMarketing}
        />
      )}
    </>
  )
}

interface CompletedInfoProps {
  customer: any
  onClickEdit: () => void
}

/** Renders the customer's info in a compact format after it's been entered and validated */
const CompletedInfo = (props: CompletedInfoProps) => {
  const {
    smsAccountEnabled,
    subscribeToSmsMarketing,
    setSubscribeToSmsMarketing,
    subscribeToEmailMarketing,
    setSubscribeToEmailMarketing
  } = useMarketing()
  const phoneNumber = useWatch({ name: 'yourInfoPhone' })
  const email = useWatch({ name: 'yourInfoEmail' })
  const firstName = useWatch({ name: 'yourInfoFirstName' })
  const lastName = useWatch({ name: 'yourInfoLastName' })
  const parsedPhoneNumber = useMemo(() => {
    return isValidPhoneNumber(phoneNumber ?? '')
      ? parsePhoneNumber(phoneNumber)
      : null
  }, [phoneNumber])

  return (
    <div>
      <div className='completedInfoContainer'>
        <div className='completedInfo'>
          {firstName && lastName && (
            <div className='name'>
              {firstName} {lastName}
            </div>
          )}
          {email && <div className='accountInfo'>{email}</div>}
          {parsedPhoneNumber?.isValid() && (
            <div className='accountInfo'>
              {parsedPhoneNumber.formatInternational()}
            </div>
          )}
        </div>
        <div>
          {props.customer && (
            <div
              tabIndex={0}
              className='editLink'
              aria-label={t('customer-info-section.edit-your-user-info')}
              role='button'
              onKeyDown={(e: React.KeyboardEvent) => {
                switch (e.key) {
                  case ' ':
                  case 'Enter':
                    props.onClickEdit()
                    break
                  default:
                    break
                }
              }}
              onClick={props.onClickEdit}
            >
              {t('customer-info-section.edit')}
            </div>
          )}
        </div>
      </div>
      {parsedPhoneNumber?.isValid() && smsAccountEnabled && (
        <MarketingSubscriptionCheckbox
          name='subscribeToSmsMarketing'
          legalText={MarketingSubscriptionCheckboxLegalText.SMS}
          isActive={subscribeToSmsMarketing}
          setActive={setSubscribeToSmsMarketing}
        />
      )}
      <MarketingSubscriptionCheckbox
        name='subscribeToEmailMarketing'
        legalText={MarketingSubscriptionCheckboxLegalText.EMAIL}
        isActive={subscribeToEmailMarketing}
        setActive={setSubscribeToEmailMarketing}
      />
    </div>
  )
}

export default CustomerInfoSection
